if (!customElements.get('device-power-selector')) {
  customElements.define(
    'device-power-selector',
    class DevicePowerSelector extends HTMLElement {
      constructor() {
        super();

        this.state = {
          step: 1,
          selectedCategoryGid: null,
          selectedOptionGids: new Set(),
          selectedOptionQtyByGid: new Map(),
          options: [],
          loadingOptions: false,
          optionsError: null,
        };

        this.cache = {
          optionsByType: new Map(),
          categoryLevel2NameByGid: new Map(),
          categoryLevel2SortByGid: new Map(),
          categoryLevel2FetchInFlight: new Set(),
          products: null,
          loadingProducts: false,
          productsError: null,
          startingWattValuesByCollectionHandle: new Map(),
          startingWattValuesPromiseByCollectionHandle: new Map(),
        };

        this.dom = {};
      }

      connectedCallback() {
        this.init();
      }

      init() {
        const configEl = this.querySelector('[data-device-power-selector-config]');
        if (!configEl) return;

        try {
          this.data = JSON.parse(configEl.textContent || '{}');
        } catch (e) {
          console.error('[device-power-selector] Invalid JSON config', e);
          return;
        }

        this.dom = {
          stepIndicators: Array.from(this.querySelectorAll('[data-dps-step-indicator]')),
          panels: {
            1: this.querySelector('[data-dps-panel="1"]'),
            2: this.querySelector('[data-dps-panel="2"]'),
            3: this.querySelector('[data-dps-panel="3"]'),
          },
          categories: this.querySelector('[data-dps-categories]'),
          options: this.querySelector('[data-dps-options]'),
          step2Category: this.querySelector('[data-dps-step2-category]'),
          nextBtn: this.querySelector('[data-dps-next]'),
          backTo1: this.querySelector('[data-dps-back-to-step="1"]'),
          backTo2: this.querySelector('[data-dps-back-to-step="2"]'),
          recommendations: this.querySelector('[data-dps-recommendations]'),
          emptyNote: this.querySelector('[data-dps-empty-note]'),
          recommendationMessage: this.querySelector('[data-dps-recommendation-message]'),
          refineRow: this.querySelector('[data-dps-refine-row]'),
          refineLink: this.querySelector('[data-dps-refine-link]'),
          runningWatts: this.querySelector('[data-dps-running-watts]'),
          startingWatts: this.querySelector('[data-dps-starting-watts]'),
          wattageDetails: this.querySelector('[data-dps-wattage-details]'),
          wattageDetailsBody: this.querySelector('[data-dps-wattage-details-body]'),
        };

        const templates = Array.from(this.querySelectorAll('[data-dps-product-card-template]'));
        this.productCardTemplates = templates.length
          ? new Map(templates.map((t) => [String(t.dataset.dpsProductCardTemplate), t]))
          : null;

        this.bindEvents();
        this.renderStep1();
        this.goToStep(1);
      }

      bindEvents() {
        this.addEventListener('click', (e) => {
          // Don't toggle selection when interacting with qty input
          if (e.target.closest('[data-dps-option-qty-input]')) return;

          const catBtn = e.target.closest('[data-dps-category-gid]');
          if (catBtn) {
            e.preventDefault();
            this.onSelectCategory(catBtn.dataset.dpsCategoryGid);
            return;
          }

          const optToggle = e.target.closest('[data-dps-option-gid]');
          if (optToggle) {
            e.preventDefault();
            this.toggleOption(optToggle.dataset.dpsOptionGid);
            return;
          }

          const back1 = e.target.closest('[data-dps-back-to-step="1"]');
          if (back1) {
            e.preventDefault();
            this.goToStep(1);
            return;
          }

          const back2 = e.target.closest('[data-dps-back-to-step="2"]');
          if (back2) {
            e.preventDefault();
            this.goToStep(2);
            return;
          }

          const next = e.target.closest('[data-dps-next]');
          if (next) {
            e.preventDefault();
            this.goToStep(3);
            return;
          }

          const detailsToggle = e.target.closest('[data-dps-wattage-details-toggle]');
          if (detailsToggle) {
            e.preventDefault();
            this.toggleWattageDetails(detailsToggle);
            return;
          }
        });

        this.addEventListener('input', (e) => {
          const input = e.target.closest('[data-dps-option-qty-input]');
          if (!input) return;
          const gid = input.dataset.dpsOptionQtyInput;
          this.onQtyInputChange(gid, input.value);
        });

        this.addEventListener('change', (e) => {
          const input = e.target.closest('[data-dps-option-qty-input]');
          if (!input) return;
          const gid = input.dataset.dpsOptionQtyInput;
          this.onQtyInputChange(gid, input.value);
        });
      }

      goToStep(step) {
        this.state.step = step;

        // Panels
        Object.entries(this.dom.panels).forEach(([k, el]) => {
          if (!el) return;
          const isActive = Number(k) === step;
          el.classList.toggle('hidden', !isActive);
        });

        // Indicators
        this.dom.stepIndicators.forEach((btn) => {
          const s = Number(btn.dataset.dpsStepIndicator);
          btn.classList.toggle('dps__step--active', s === step);
          btn.classList.toggle('dps__step--complete', s < step);
          btn.classList.toggle('dps__step--upcoming', s > step);

          // Enable logic
          if (s === 1) btn.disabled = false;
          if (s === 2) btn.disabled = !this.state.selectedCategoryGid;
          if (s === 3) btn.disabled = this.state.selectedOptionGids.size === 0;
        });

        // Step-specific rendering
        if (step === 2) this.renderStep2();
        if (step === 3) this.renderStep3();
      }

      renderStep1() {
        if (!this.dom.categories) return;
        const categories = Array.isArray(this.data?.categories) ? this.data.categories : [];

        const sorted = [...categories].sort((a, b) => {
          const sa = Number(a.sort ?? 0);
          const sb = Number(b.sort ?? 0);
          if (sa !== sb) return sa - sb;
          return String(a.name ?? '').localeCompare(String(b.name ?? ''));
        });

        this.dom.categories.innerHTML = sorted
          .map((c) => {
            const isSelected = this.state.selectedCategoryGid && this.state.selectedCategoryGid === c.gid;
            const img = c.image
              ? `<div class="dps__card-img"><img src="${this.escapeHtml(c.image)}" alt="${this.escapeHtml(
                  c.name || ''
                )}" loading="lazy"></div>`
              : `<div class="dps__card-img dps__card-img--placeholder"></div>`;

            return `
              <button class="dps__card ${isSelected ? 'is-selected' : ''}" type="button" data-dps-category-gid="${this.escapeHtml(
                c.gid || ''
              )}">
                ${img}
                <div class="dps__card-name">${this.escapeHtml(c.name || '')}</div>
              </button>
            `;
          })
          .join('');
      }

      normalizeMetaobjectGid(value) {
        const raw = String(value ?? '').trim();
        if (!raw) return '';
        if (raw.startsWith('gid://')) return raw;
        if (/^\d+$/.test(raw)) return `gid://shopify/Metaobject/${raw}`;
        return raw;
      }

      onSelectCategory(gid) {
        this.state.selectedCategoryGid = this.normalizeMetaobjectGid(gid);
        this.state.selectedOptionGids = new Set(); // reset options when switching category
        this.state.selectedOptionQtyByGid = new Map();
        this.state.options = [];
        this.state.optionsError = null;
        if (this.dom.nextBtn) this.dom.nextBtn.disabled = true;

        this.renderStep1();
        this.goToStep(2);

        // Load device_power_option via Storefront API, then render step 2 groups.
        this.loadDevicePowerOptionsForSelectedCategory().catch((e) => {
          console.error(e);
        });
      }

      getSelectedCategory() {
        const categories = Array.isArray(this.data?.categories) ? this.data.categories : [];
        const selected = this.normalizeMetaobjectGid(this.state.selectedCategoryGid);
        return categories.find((c) => this.normalizeMetaobjectGid(c.gid) === selected) || null;
      }

      getOptionByGid(gid) {
        const normalized = this.normalizeMetaobjectGid(gid);
        const options = Array.isArray(this.state.options) ? this.state.options : [];
        return options.find((o) => this.normalizeMetaobjectGid(o.gid) === normalized) || null;
      }

      async loadDevicePowerOptionsForSelectedCategory() {
        if (!this.dom.options) return;

        const meta = this.data?.meta || {};
        const optionType = meta.device_power_option_type;
        const storefrontApi = meta.storefront_api || {};
        const token = storefrontApi.access_token || '';
        const pageSize = Number(storefrontApi.device_power_option_page_size || 250);

        if (!token || !optionType) {
          this.state.options = [];
          this.state.optionsError = 'Storefront API token or metaobject type not configured.';
          this.state.loadingOptions = false;
          this.renderStep2();
          return;
        }

        this.state.loadingOptions = true;
        this.state.optionsError = null;
        this.renderStep2(); // show loading

        const allOptions = await this.fetchAllDevicePowerOptions(optionType, pageSize);
        const selectedCategoryGid = this.normalizeMetaobjectGid(this.state.selectedCategoryGid);
        this.state.options = allOptions.filter(
          (o) => this.normalizeMetaobjectGid(o.category_level1_gid) && this.normalizeMetaobjectGid(o.category_level1_gid) === selectedCategoryGid
        );
        this.state.loadingOptions = false;
        this.renderStep2();
      }

      async fetchAllDevicePowerOptions(type, pageSize) {
        if (this.cache.optionsByType.has(type)) return this.cache.optionsByType.get(type);

        const query = `
          query DevicePowerOptions($type: String!, $first: Int!, $after: String) {
            metaobjects(type: $type, first: $first, after: $after) {
              edges {
                cursor
                node {
                  id
                  handle
                  fields {
                    key
                    value
                  }
                }
              }
              pageInfo {
                hasNextPage
                endCursor
              }
            }
          }
        `;

        const all = [];
        let after = null;
        let safety = 0;

        while (safety < 20) {
          safety += 1;
          const data = await this.storefrontGraphql(query, {
            type,
            first: Math.min(250, Math.max(1, pageSize || 250)),
            after,
          });

          const conn = data?.metaobjects;
          const edges = Array.isArray(conn?.edges) ? conn.edges : [];

          edges.forEach((edge) => {
            const node = edge?.node;
            if (!node?.id) return;
            all.push(this.mapDevicePowerOptionNode(node));
          });

          if (!conn?.pageInfo?.hasNextPage) break;
          after = conn.pageInfo.endCursor;
          if (!after) break;
        }

        // Remove invalid/empty entries
        const normalized = all.filter((x) => x && x.gid);
        this.cache.optionsByType.set(type, normalized);
        return normalized;
      }

      mapDevicePowerOptionNode(node) {
        const fields = Array.isArray(node.fields) ? node.fields : [];
        const map = fields.reduce((acc, f) => {
          if (!f?.key) return acc;
          acc[f.key] = f.value;
          return acc;
        }, {});

        return {
          gid: node.id,
          category_level1_gid: this.normalizeMetaobjectGid(map.category_level1 || ''),
          category_level2: map.category_level2 || '',
          device_name: map.device_name || '',
          display_name: map.display_name || '',
          wattage: Number(map.wattage || 0),
          starting_wattage: Number(map.starting_wattage || 0),
          default_quantity: Number(map.default_quantity || 0),
        };
      }

      toggleWattageDetails(toggleEl) {
        if (!this.dom.wattageDetails) return;
        const expanded = toggleEl.getAttribute('aria-expanded') === 'true';
        const next = !expanded;
        toggleEl.setAttribute('aria-expanded', next ? 'true' : 'false');
        this.dom.wattageDetails.setAttribute('aria-hidden', next ? 'false' : 'true');
        this.dom.wattageDetails.hidden = !next;
      }

      getCategoryLevel2Title(rawValue) {
        const raw = String(rawValue || '').trim();
        if (!raw) return '';
        const gid = this.normalizeMetaobjectGid(raw);
        if (!gid.startsWith('gid://')) return raw;
        return this.cache.categoryLevel2NameByGid.get(gid) || raw;
      }

      getCategoryLevel2Sort(rawValue) {
        const raw = String(rawValue || '').trim();
        if (!raw) return 0;
        const gid = this.normalizeMetaobjectGid(raw);
        if (!gid.startsWith('gid://')) return 0;
        return Number(this.cache.categoryLevel2SortByGid.get(gid)) || 0;
      }

      async ensureCategoryLevel2Names(values) {
        const unique = Array.from(new Set(values.map((v) => this.normalizeMetaobjectGid(v)).filter(Boolean)));
        const toFetch = unique.filter(
          (gid) => gid.startsWith('gid://') && !this.cache.categoryLevel2NameByGid.has(gid) && !this.cache.categoryLevel2FetchInFlight.has(gid)
        );
        if (!toFetch.length) return;

        toFetch.forEach((gid) => this.cache.categoryLevel2FetchInFlight.add(gid));

        try {
          await Promise.all(
            toFetch.map(async (gid) => {
              try {
                const data = await this.storefrontGraphql(
                  `
                    query NodeMetaobjectFields($id: ID!) {
                      node(id: $id) {
                        ... on Metaobject {
                          id
                          fields {
                            key
                            value
                          }
                        }
                      }
                    }
                  `,
                  { id: gid }
                );

                const node = data?.node;
                const fields = Array.isArray(node?.fields) ? node.fields : [];
                const nameField = fields.find((f) => f?.key === 'name') || fields.find((f) => f?.key === 'title');
                const label = String(nameField?.value || '').trim();
                if (label) this.cache.categoryLevel2NameByGid.set(gid, label);
                const sortField = fields.find((f) => f?.key === 'sort');
                const sortVal = Number(sortField?.value ?? 0);
                if (Number.isFinite(sortVal)) this.cache.categoryLevel2SortByGid.set(gid, sortVal);
              } finally {
                this.cache.categoryLevel2FetchInFlight.delete(gid);
              }
            })
          );
        } finally {
          if (this.state.step === 2) this.renderStep2();
        }
      }

      async storefrontGraphql(query, variables) {
        const meta = this.data?.meta || {};
        const storefrontApi = meta.storefront_api || {};
        const token = storefrontApi.access_token || '';
        const version = storefrontApi.version || '2025-10';

        const endpoint = `${window.location.origin}/api/${encodeURIComponent(version)}/graphql.json`;
        const res = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({ query, variables }),
        });

        const json = await res.json();
        if (!res.ok || json.errors) {
          const msg = json?.errors?.[0]?.message || `Storefront API error (${res.status})`;
          throw new Error(msg);
        }
        return json.data;
      }

      renderStep2() {
        if (!this.dom.options) return;

        // Step 2 left: selected category (image + name)
        if (this.dom.step2Category) {
          const c = this.getSelectedCategory();
          if (c) {
            const img = c.image
              ? `<div class="dps__step2-category-img"><img src="${this.escapeHtml(c.image)}" alt="${this.escapeHtml(
                  c.name || ''
                )}" loading="lazy"></div>`
              : `<div class="dps__step2-category-img"></div>`;
            this.dom.step2Category.innerHTML = `
              <div class="dps__step2-category-header">
                ${img}
                <div class="dps__step2-category-name">${this.escapeHtml(c.name || '')}</div>
              </div>
            `;
          } else {
            this.dom.step2Category.innerHTML = '';
          }
        }

        if (this.state.loadingOptions) {
          this.dom.options.innerHTML = `<div class="dps__empty">加载中...</div>`;
          if (this.dom.nextBtn) this.dom.nextBtn.disabled = true;
          return;
        }

        if (this.state.optionsError) {
          this.dom.options.innerHTML = `<div class="dps__empty">${this.escapeHtml(this.state.optionsError)}</div>`;
          if (this.dom.nextBtn) this.dom.nextBtn.disabled = true;
          return;
        }

        const filtered = Array.isArray(this.state.options) ? this.state.options : [];

        const groups = new Map();
        filtered.forEach((o) => {
          const key = o.category_level2 || '';
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(o);
        });

        this.ensureCategoryLevel2Names(Array.from(groups.keys())).catch(() => {});

        const groupHtml = Array.from(groups.entries())
          .sort((a, b) => {
            const sortA = this.getCategoryLevel2Sort(a[0]);
            const sortB = this.getCategoryLevel2Sort(b[0]);
            if (sortA !== sortB) return sortA - sortB;
            return String(this.getCategoryLevel2Title(a[0])).localeCompare(String(this.getCategoryLevel2Title(b[0])));
          })
          .map(([groupName, items]) => {
            const title = this.getCategoryLevel2Title(groupName);
            const itemsHtml = items
              .sort((a, b) => String(a.device_name || '').localeCompare(String(b.device_name || '')))
              .map((item) => {
                const key = this.normalizeMetaobjectGid(item.gid);
                const checked = this.state.selectedOptionGids.has(key);
                const defaultQty = Math.max(1, Number(item.default_quantity ?? 1));
                const currentQty = Math.max(
                  defaultQty,
                  Number(this.state.selectedOptionQtyByGid.get(key) ?? defaultQty) || defaultQty
                );
                return `
                  <button class="dps__option ${checked ? 'is-selected' : ''}" type="button" data-dps-option-gid="${this.escapeHtml(key)}">
                    <span class="dps__checkbox" aria-hidden="true">${checked ? '✓' : ''}</span>
                    <span class="dps__option-name">${this.escapeHtml(item.device_name || '')}</span>
                    <span class="dps__option-qty ${checked ? '' : 'hidden'}">
                      <input
                        class="dps__option-qty-input"
                        data-dps-option-qty-input="${this.escapeHtml(key)}"
                        type="number"
                        inputmode="numeric"
                        min="${defaultQty}"
                        value="${currentQty}"
                      />
                    </span>
                  </button>
                `;
              })
              .join('');

            return `
              <div class="dps__group">
                <div class="dps__group-title">${this.escapeHtml(title || '')}</div>
                <div class="dps__group-items">${itemsHtml}</div>
              </div>
            `;
          })
          .join('');

        this.dom.options.innerHTML = groupHtml || `<div class="dps__empty">该分类下暂无可选设备。</div>`;
        if (this.dom.nextBtn) this.dom.nextBtn.disabled = this.state.selectedOptionGids.size === 0;
       // this.applyMasonryGridSpans();
      }
       applyMasonryGridSpans() {
        if (!this.dom.options) return;
        const run = () => {
          const groups = this.dom.options.querySelectorAll('.dps__group');
          groups.forEach((el) => {
            const count = el.querySelectorAll('.dps__option').length;
            const span = Math.max(1, count); // 二级去重后的长度 = 该分组内选项个数
            el.style.gridRow = `span ${span}`;
          });
        };
        requestAnimationFrame(() => requestAnimationFrame(run));
      }
      onQtyInputChange(gid, rawValue) {
        const option = this.getOptionByGid(gid);
        if (!option) return;
        const key = this.normalizeMetaobjectGid(option.gid);
        if (!this.state.selectedOptionGids.has(key)) return;

        const min = Math.max(1, Number(option.default_quantity ?? 1));
        let next = Number(rawValue);
        if (!Number.isFinite(next)) next = min;
        next = Math.max(min, Math.trunc(next));
        this.state.selectedOptionQtyByGid.set(key, next);
      }

      toggleOption(gid) {
        if (!gid) return;
        const option = this.getOptionByGid(gid);
        const key = this.normalizeMetaobjectGid(option?.gid || gid);

        if (this.state.selectedOptionGids.has(key)) {
          this.state.selectedOptionGids.delete(key);
          this.state.selectedOptionQtyByGid.delete(key);
        } else {
          this.state.selectedOptionGids.add(key);
          const min = Math.max(1, Number(option?.default_quantity ?? 1));
          this.state.selectedOptionQtyByGid.set(key, min);
        }

        if (this.dom.nextBtn) this.dom.nextBtn.disabled = this.state.selectedOptionGids.size === 0;
        this.renderStep2();
      }

      renderStep3() {
        const options = Array.isArray(this.state.options) ? this.state.options : [];
        const selectedSet = this.state.selectedOptionGids;

        const selected = options.filter((o) => selectedSet.has(this.normalizeMetaobjectGid(o.gid)));

        const rows = selected.map((o) => {
          const key = this.normalizeMetaobjectGid(o.gid);
          const qty = Math.max(1, Number(this.state.selectedOptionQtyByGid.get(key) ?? o.default_quantity ?? 1));
          const running = Math.trunc(Number(o.wattage ?? 0) * qty);
          const starting = Math.trunc(Number(o.starting_wattage ?? 0) * qty);
          return {
            gid: o.gid,
            name: o.display_name || o.device_name || '',
            qty,
            running,
            starting,
          };
        });

        const runningTotal = rows.reduce((sum, r) => sum + r.running, 0);
        const maxExtra = rows.reduce((max, r) => Math.max(max, r.starting - r.running), 0);
        const startingNeeded = runningTotal + Math.max(0, maxExtra);

        if (this.dom.runningWatts) this.dom.runningWatts.textContent = String(runningTotal);
        if (this.dom.startingWatts) this.dom.startingWatts.textContent = String(startingNeeded);

        if (this.dom.wattageDetailsBody) {
          this.dom.wattageDetailsBody.innerHTML = rows
            .map((r) => {
              const name = r.qty > 1 ? `${r.name} ×${r.qty}` : r.name;
              return `
                <tr>
                  <td>${this.escapeHtml(name)}</td>
                  <td class="u-textRight u-textNoWrap">${this.escapeHtml(String(r.running))}</td>
                  <td class="u-textRight u-textNoWrap">${this.escapeHtml(String(r.starting || 0))}</td>
                </tr>
              `;
            })
            .join('');
        }

        // Collapse by default whenever we enter step 3
        const toggle = this.querySelector('[data-dps-wattage-details-toggle]');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
        if (this.dom.wattageDetails) {
          this.dom.wattageDetails.setAttribute('aria-hidden', 'true');
          this.dom.wattageDetails.hidden = true;
        }

        this.renderRecommendations(runningTotal, Math.trunc(startingNeeded));
      }

      renderRecommendations(requiredRunning, requiredStarting) {
        const meta = this.data?.meta || {};
        const storefrontApi = meta.storefront_api || {};
        const useStorefrontProducts = Boolean(storefrontApi.use_storefront_products);

        // Remember latest criteria so async fetch can rerender.
        this.lastRecommendationCriteria = {
          running: Number(requiredRunning ?? 0),
          starting: Number(requiredStarting ?? 0),
        };

        // B mode: fetch products from Storefront API with loading
        if (useStorefrontProducts) {
          if (!this.cache.products && !this.cache.loadingProducts) {
            this.loadAllStoreProducts().catch((e) => {
              console.error(e);
              this.cache.productsError = e?.message || 'Failed to load products.';
              this.cache.loadingProducts = false;
              const c = this.lastRecommendationCriteria || { running: requiredRunning, starting: requiredStarting };
              this.renderRecommendations(c.running, c.starting);
            });
          }

          if (this.cache.loadingProducts) {
            if (this.dom.recommendationMessage) {
              this.dom.recommendationMessage.textContent = 'Loading generators...';
            }
            if (this.dom.refineRow) this.dom.refineRow.hidden = true;
            if (this.dom.emptyNote) this.dom.emptyNote.classList.add('hidden');
            this.dom.recommendations.innerHTML = '';
            return;
          }

          if (this.cache.productsError) {
            if (this.dom.recommendationMessage) {
              this.dom.recommendationMessage.textContent = this.cache.productsError;
            }
            if (this.dom.refineRow) this.dom.refineRow.hidden = true;
            if (this.dom.emptyNote) this.dom.emptyNote.classList.remove('hidden');
            this.dom.recommendations.innerHTML = '';
            return;
          }
        }

        const products = useStorefrontProducts
          ? Array.isArray(this.cache.products)
            ? this.cache.products
            : []
          : Array.isArray(this.data?.products)
          ? this.data.products
          : [];

        if (!this.dom.recommendations) return;

        const candidates = products
          .map((p) => ({
            ...p,
            running_watt: Number(p.running_watt ?? 0),
            starting_watt: Number(p.starting_watt ?? 0),
          }))
          .filter((p) => p.id != null);

        const meets = candidates.filter((p) => p.running_watt >= requiredRunning && p.starting_watt >= requiredStarting);
        const meetsCount = meets.length;

        this.updateRefineResultsLink({
          meetsCount,
          requiredStarting: Number(requiredStarting ?? 0),
        });

        const primarySorted = meets
          .slice()
          .sort((a, b) => {
            const sa = (a.running_watt - requiredRunning) + (a.starting_watt - requiredStarting);
            const sb = (b.running_watt - requiredRunning) + (b.starting_watt - requiredStarting);
            if (sa !== sb) return sa - sb;
            if (a.starting_watt !== b.starting_watt) return a.starting_watt - b.starting_watt;
            return String(a.title ?? '').localeCompare(String(b.title ?? ''));
          });
          
        const top = [];
        primarySorted.forEach((p) => {
          if (top.length < 5 && p.inStock !== false) top.push(p);
        });
        console.log(primarySorted,"primarySorted")
        if (top.length < 5) {
          const chosenIds = new Set(top.map((p) => String(p.id)));
          const rest = candidates.filter((p) => !chosenIds.has(String(p.id)));
          rest
            .map((p) => {
              const rd = Math.max(0, requiredRunning - p.running_watt);
              const sd = Math.max(0, requiredStarting - p.starting_watt);
              const re = Math.max(0, p.running_watt - requiredRunning);
              const se = Math.max(0, p.starting_watt - requiredStarting);
              const score = 10 * rd + 10 * sd + 1 * re + 1 * se;
              return { p, score };
            })
            .sort((a, b) => {
              if (a.score !== b.score) return a.score - b.score;
              return String(a.p.title ?? '').localeCompare(String(b.p.title ?? ''));
            })
            .forEach(({ p }) => {
              if (top.length < 5 && p.inStock !== false) top.push(p);
            });
        }

        if (this.dom.recommendationMessage) {
          this.dom.recommendationMessage.textContent =
            `Good news!\nWe found ${meetsCount} generators that meet or exceed your power needs. Here are the top five matches:`;
          this.dom.recommendationMessage.style.whiteSpace = 'pre-line';
        }

        if (this.dom.emptyNote) this.dom.emptyNote.classList.toggle('hidden', top.length > 0);

        this.dom.recommendations.innerHTML = '';
        top.forEach((p) => {
          // If we have server-rendered templates (collection mode), reuse them.
          const template = this.productCardTemplates?.get?.(String(p.id));
          if (template?.content) {
            this.dom.recommendations.appendChild(template.content.cloneNode(true));
          } else {
            // Storefront mode fallback: render a lightweight card
            this.dom.recommendations.appendChild(this.renderGeneratorCard(p));
          }
        });
      }

      renderGeneratorCard(p) {
        console.log(p,"producrsList")
        const wrapper = document.createElement('div');
        wrapper.className = 'dps__product';

        const img = p.image
          ? `<div class="dps__product-img"><img src="${this.escapeHtml(p.image)}" alt="${this.escapeHtml(
              p.title || ''
            )}" loading="lazy"></div>`
          : `<div class="dps__product-img dps__product-img--placeholder"></div>`;

        const running = Number(p.running_watt ?? 0);
        const starting = Number(p.starting_watt ?? 0);
        const url = p.url || '#';

        wrapper.innerHTML = `
          <a class="dps__product-link" href="${this.escapeHtml(url)}">
            ${img}
          </a>
          <div class="dps__product-body">
            <a class="dps__product-title dps__product-link" href="${this.escapeHtml(url)}">${this.escapeHtml(p.title || '')}</a>
            <div class="dps__product-meta">
              <span class="dps__product-price">${this.escapeHtml(p.price || '')}</span>
              <span class="dps__product-originalPrice"><del>${this.escapeHtml(p.originalPrice || '')}<del></span>
            </div>
            <div class="product-card__custom-fields text-sm mt-2 space-y-1">
              <div class="custom-field flex items-center">
                <span class="font-medium">${this.escapeHtml(String(running || 0))}</span>
                <span class="text-subtext ml-2">&nbsp;Running Watt</span>
              </div>
              <div class="custom-field flex items-center">
                <span class="font-medium">${this.escapeHtml(String(starting || 0))}</span>
                <span class="text-subtext ml-2">&nbsp;Starting Watt</span>
              </div>
            </div>
            <div class="dps__product-actions">
              <a class="btn" href="${this.escapeHtml(url)}">Details</a>
            </div>
          </div>
        `;

        return wrapper;
      }

      async loadAllStoreProducts() {
        const meta = this.data?.meta || {};
        const storefrontApi = meta.storefront_api || {};
        const pageSize = Math.min(250, Math.max(1, Number(storefrontApi.storefront_products_page_size || 250)));
        const maxPages = Math.min(20, Math.max(1, Number(storefrontApi.storefront_products_max_pages || 10)));

        this.cache.loadingProducts = true;
        this.cache.productsError = null;
        if (this.dom.recommendationMessage) this.dom.recommendationMessage.textContent = 'Loading generators...';

        const query = `
          query ProductsPage($first: Int!, $after: String) {
            products(first: $first, after: $after) {
              pageInfo { hasNextPage endCursor }
              edges {
                node {
                  id
                  title
                  handle
                  onlineStoreUrl
                  featuredImage { url }
                  priceRange { minVariantPrice { amount currencyCode } }
                  compareAtPriceRange { minVariantPrice { amount currencyCode } }
                  running: metafield(namespace: "custom", key: "running_watt") { value }
                  starting: metafield(namespace: "custom", key: "starting_watt") { value }
                  variants(first: 10) {
                    edges {
                      node {
                        availableForSale
                      }
                    }
                  }
                }
              }
            }
          }
        `;

        try {
          const all = [];
          let after = null;
          for (let page = 1; page <= maxPages; page++) {
            if (this.dom.recommendationMessage) {
              this.dom.recommendationMessage.textContent = `Loading generators... (${page}/${maxPages})`;
            }

            const data = await this.storefrontGraphql(query, { first: pageSize, after });
            const conn = data?.products;
            const edges = Array.isArray(conn?.edges) ? conn.edges : [];

            edges.forEach((edge) => {
              const n = edge?.node;
              if (!n?.id) return;
              const priceAmount = n?.priceRange?.minVariantPrice?.amount;
              const currency = n?.priceRange?.minVariantPrice?.currencyCode;
              const originalAmount = n?.compareAtPriceRange?.minVariantPrice?.amount;
              const originalCurrency = n?.compareAtPriceRange?.minVariantPrice?.currencyCode;
              const variantEdges = Array.isArray(n?.variants?.edges) ? n.variants.edges : [];
              const inStock = variantEdges.some((e) => e?.node?.availableForSale === true);
              all.push({
                id: String(n.id).includes('gid://') ? String(n.id).split('/').pop() : n.id,
                gid: n.id,
                title: n.title,
                url: n.onlineStoreUrl || (n.handle ? `/products/${n.handle}` : ''),
                image: n.featuredImage?.url || '',
                price: priceAmount ? `${priceAmount} ${currency || ''}`.trim() : '',
                // 原价（可能为 null，如果商品没有设置对比价格）
                originalPrice: originalAmount ? `${originalAmount} ${originalCurrency || ''}`.trim() : '',
                running_watt: Number(n.running?.value || 0),
                starting_watt: Number(n.starting?.value || 0),
                inStock,
              });
            });

            if (!conn?.pageInfo?.hasNextPage) break;
            after = conn.pageInfo.endCursor;
            if (!after) break;
          }

          this.cache.products = all;
        } catch (e) {
          this.cache.productsError = e?.message || 'Failed to load products.';
          throw e;
        } finally {
          this.cache.loadingProducts = false;
          const c = this.lastRecommendationCriteria;
          if (c) this.renderRecommendations(c.running, c.starting);
        }
      }

      async loadStartingWattValuesForCollectionHandle(handle) {
        const safeHandle = String(handle || '').trim();
        if (!safeHandle) return [];

        const cached = this.cache.startingWattValuesByCollectionHandle.get(safeHandle);
        if (Array.isArray(cached)) return cached;

        const inFlight = this.cache.startingWattValuesPromiseByCollectionHandle.get(safeHandle);
        if (inFlight) return await inFlight;

        const meta = this.data?.meta || {};
        const storefrontApi = meta.storefront_api || {};
        const pageSize = Math.min(250, Math.max(1, Number(storefrontApi.storefront_products_page_size || 250)));
        const maxPages = Math.min(20, Math.max(1, Number(storefrontApi.storefront_products_max_pages || 10)));

        const refine = meta.refine_results || {};
        const mfNs = String(refine?.filter_metafield?.namespace || 'custom');
        const mfKey = String(refine?.filter_metafield?.key || 'starting_watt');

        const query = `
          query CollectionStartingWatts($handle: String!, $first: Int!, $after: String, $namespace: String!, $key: String!) {
            collection(handle: $handle) {
              products(first: $first, after: $after) {
                pageInfo { hasNextPage endCursor }
                edges {
                  node {
                    starting: metafield(namespace: $namespace, key: $key) { value }
                  }
                }
              }
            }
          }
        `;

        const promise = (async () => {
          const values = new Set();
          let after = null;

          for (let page = 1; page <= maxPages; page++) {
            const data = await this.storefrontGraphql(query, {
              handle: safeHandle,
              first: pageSize,
              after,
              namespace: mfNs,
              key: mfKey,
            });

            const conn = data?.collection?.products;
            const edges = Array.isArray(conn?.edges) ? conn.edges : [];

            edges.forEach((edge) => {
              const raw = edge?.node?.starting?.value;
              const n = Number(raw);
              if (!Number.isFinite(n)) return;
              values.add(Math.trunc(n));
            });

            if (!conn?.pageInfo?.hasNextPage) break;
            after = conn.pageInfo.endCursor;
            if (!after) break;
          }

          const arr = Array.from(values).filter((n) => Number.isFinite(n)).sort((a, b) => a - b);
          this.cache.startingWattValuesByCollectionHandle.set(safeHandle, arr);
          return arr;
        })();

        this.cache.startingWattValuesPromiseByCollectionHandle.set(safeHandle, promise);

        try {
          return await promise;
        } finally {
          this.cache.startingWattValuesPromiseByCollectionHandle.delete(safeHandle);
        }
      }

      updateRefineResultsLink({ meetsCount, requiredStarting }) {
        if (!this.dom.refineRow || !this.dom.refineLink) return;

        const meta = this.data?.meta || {};
        const refine = meta.refine_results || {};
        const baseUrl = String(refine.collection_url || '').trim();
        const handle = String(refine.collection_handle || '').trim();
        const sortBy = String(refine.sort_by || 'no-ascending').trim();
        const filterParam = String(refine.filter_param || 'filter.p.m.custom.starting_watt').trim();

        if (!baseUrl) {
          this.dom.refineRow.hidden = true;
          return;
        }

        // Set button label
        const template = this.dom.refineLink.getAttribute('data-dps-refine-template') || '';
        this.dom.refineLink.textContent = template.includes('__COUNT__')
          ? template.replace('__COUNT__', String(meetsCount))
          : `Refine ${meetsCount} Results`;

        // Base href (always include sort_by)
        const url = new URL(baseUrl, window.location.origin);
        if (sortBy) url.searchParams.set('sort_by', sortBy);

        // Default: no filter until we know V
        url.searchParams.delete(filterParam);
        this.dom.refineLink.setAttribute('href', `${url.pathname}?${url.searchParams.toString()}`);
        this.dom.refineRow.hidden = false;

        // Need V to decide filter parameter
        if (!handle) return;

        const cached = this.cache.startingWattValuesByCollectionHandle.get(handle);
        if (!Array.isArray(cached)) {
          this.loadStartingWattValuesForCollectionHandle(handle)
            .then(() => {
              const c = this.lastRecommendationCriteria;
              if (!c) return;
              // Re-apply with latest criteria
              this.updateRefineResultsLink({
                meetsCount: meetsCount,
                requiredStarting: Number(c.starting ?? 0),
              });
            })
            .catch(() => {
              // On failure, keep base link (no filter)
            });
          return;
        }

        const values = cached;
        if (!values.length) return;

        const s = Math.trunc(Number(requiredStarting ?? 0));
        const min = values[0];
        const max = values[values.length - 1];

        if (s < min) return; // show all, no filter

        const minMatch = values.find((v) => v >= s) ?? max;
        const valuesToFilter = values
          .filter((v) => v >= minMatch && v <= max)
          .slice(0, 50);
        valuesToFilter.forEach((v) => url.searchParams.append(filterParam, String(v)));
        this.dom.refineLink.setAttribute('href', `${url.pathname}?${url.searchParams.toString()}`);
      }

      escapeHtml(input) {
        return String(input)
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;')
          .replace(/"/g, '&quot;')
          .replace(/'/g, '&#039;');
      }
    }
  );
}

